import {
  Alert,
  Box,
  CircularProgress,
  Container,
  Tab,
  Tabs,
  Typography,
} from '@mui/material';
import React, { useEffect, useState } from 'react';
import { AcrCloudCredentials } from '../../../components/AcrCloudCredentials';
import { CredentialCard } from '../../../components/CredentialCard';
import { useStudioAuthenticatedContext } from '../../../context/StudioContext';
import { StudioPage } from '../../../page-templates/StudioPage';
import * as styles from './API.styles';

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

function TabPanel(props: TabPanelProps) {
  const { children, value, index, ...other } = props;

  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`api-tabpanel-${index}`}
      aria-labelledby={`api-tab-${index}`}
      {...other}
    >
      {value === index && <Box sx={styles.tabPanel}>{children}</Box>}
    </div>
  );
}

export const API = () => {
  const { backendClient, selectedAppSet } = useStudioAuthenticatedContext();
  const [tabValue, setTabValue] = useState(0); // Start with Production tab
  const [productionCredentials, setProductionCredentials] = useState<any[]>([]);
  const [developmentCredentials, setDevelopmentCredentials] = useState<any[]>([]);
  const [acrCloudCredentials, setAcrCloudCredentials] = useState<any>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [acrCloudLoading, setAcrCloudLoading] = useState(false);

  const handleTabChange = (_: React.SyntheticEvent, newValue: number) => {
    setTabValue(newValue);
  };

  useEffect(() => {
    const fetchCredentials = async () => {
      if (!selectedAppSet) {
        setLoading(false);
        return;
      }

      try {
        setLoading(true);
        setError(null);

        // Fetch production credentials
        const productionResponse = await backendClient.getApplicationCredentials(
          selectedAppSet.productionApp.id
        );
        setProductionCredentials(productionResponse.applicationCredentials);

        // Fetch development credentials
        const developmentResponse = await backendClient.getApplicationCredentials(
          selectedAppSet.developmentApp.id
        );
        setDevelopmentCredentials(developmentResponse.applicationCredentials);

        // Fetch ACR Cloud credentials for production app
        try {
          const acrCloudResponse = await backendClient.getAcrCloudCredentials(
            selectedAppSet.productionApp.id
          );
          setAcrCloudCredentials(acrCloudResponse);
        } catch (acrErr) {
          // ACR Cloud credentials might not exist yet, which is fine
          console.log('No ACR Cloud credentials found:', acrErr);
          setAcrCloudCredentials(null);
        }
      } catch (err) {
        setError(err instanceof Error ? err.message : 'Failed to fetch credentials');
      } finally {
        setLoading(false);
      }
    };

    fetchCredentials();
  }, [backendClient, selectedAppSet]);

  const handleAcrCloudSubmit = async (accessKey: string, secretKey: string) => {
    if (!selectedAppSet) {
      throw new Error('No application selected');
    }

    setAcrCloudLoading(true);
    try {
      await backendClient.createAcrCloudCredentials(
        selectedAppSet.productionApp.id,
        accessKey,
        secretKey,
        'ACR Cloud credentials for audio fingerprinting'
      );
      
      // Refresh ACR Cloud credentials after successful creation
      try {
        const acrCloudResponse = await backendClient.getAcrCloudCredentials(
          selectedAppSet.productionApp.id
        );
        setAcrCloudCredentials(acrCloudResponse);
      } catch (refreshErr) {
        console.error('Failed to refresh ACR Cloud credentials:', refreshErr);
      }
      
      // Show success message (you could add a toast notification here)
      console.log('ACR Cloud credentials created successfully');
    } catch (error) {
      console.error('Failed to create ACR Cloud credentials:', error);
      throw error; // Re-throw to let the component handle the error
    } finally {
      setAcrCloudLoading(false);
    }
  };

  if (!selectedAppSet) {
    return (
      <StudioPage>
        <Box component="header" sx={styles.header}>
          <Typography variant="h4" color="text.primary" sx={styles.headerTitle}>
            API Keys
          </Typography>
          <Alert severity="info">
            No application selected. Please select an application to view API keys.
          </Alert>
        </Box>
      </StudioPage>
    );
  }

  return (
    <StudioPage>
      {/* Header */}
      <Box component="header" sx={styles.header}>
        <Typography variant="h4" color="text.primary" sx={styles.headerTitle}>
          API Keys
        </Typography>
        <Typography
          variant="body1"
          color="text.secondary"
          sx={styles.headerDescription}
        >
          Manage your API keys for production and development environments. Keep
          your keys secure and never share them publicly.
        </Typography>
      </Box>
      <Container maxWidth={false} sx={styles.container}>
        <Typography variant="h6" color="text.primary" sx={styles.sectionTitle}>
          Your Keys
        </Typography>

        {/* Tabs */}
        <Box sx={styles.tabsContainer}>
          <Tabs
            value={tabValue}
            onChange={handleTabChange}
            sx={styles.tabs}
          >
            <Tab label="Production" />
            <Tab label="Development" />
          </Tabs>
        </Box>

        {loading && (
          <Box sx={styles.loadingContainer}>
            <CircularProgress />
          </Box>
        )}

        {error && (
          <Alert severity="error" sx={styles.errorAlert}>
            {error}
          </Alert>
        )}

        {/* Production Tab */}
        <TabPanel value={tabValue} index={0}>
          {productionCredentials.length === 0 && !loading ? (
            <Alert severity="info">
              No production credentials found for this application.
            </Alert>
          ) : (
            <Box sx={styles.credentialsContainer}>
              {productionCredentials.map((credential) => (
                <Box key={credential.id} sx={styles.credentialCard}>
                  <CredentialCard
                    credential={credential}
                    environment="production"
                  />
                </Box>
              ))}
            </Box>
          )}
        </TabPanel>

        {/* Development Tab */}
        <TabPanel value={tabValue} index={1}>
          {developmentCredentials.length === 0 && !loading ? (
            <Alert severity="info">
              No development credentials found for this application.
            </Alert>
          ) : (
            <Box sx={styles.credentialsContainer}>
              {developmentCredentials.map((credential) => (
                <Box key={credential.id} sx={styles.credentialCard}>
                  <CredentialCard
                    credential={credential}
                    environment="development"
                  />
                </Box>
              ))}
            </Box>
          )}
        </TabPanel>

        {/* ACR Cloud Keys Section */}
        <Box sx={styles.acrCloudSection}>
          <AcrCloudCredentials
            onSubmit={handleAcrCloudSubmit}
            loading={acrCloudLoading}
            existingCredentials={acrCloudCredentials}
          />
        </Box>
      </Container>
    </StudioPage>
  );
};
